import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, LockReset as LockResetIcon, Person as PersonIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: {
    name: string;
    code: string;
  };
  isActive: boolean;
}

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  
  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null); // ID of user being acted upon

  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/system-settings/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (id: string) => {
    if (window.confirm('Are you sure you want to reset the password for this user?')) {
      try {
        const response = await axios.post(`/system-settings/users/${id}/reset-password`);
        setNewPassword(response.data.newPassword);
        setResetDialogOpen(true);
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Failed to reset password');
      }
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setActionLoading(userToDelete.id);
    try {
      await axios.delete(`/system-settings/users/${userToDelete.id}`);
      fetchUsers();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (user: User) => {
    // Optimistic update
    const originalUsers = [...users];
    setUsers(users.map(u => u.id === user.id ? { ...u, isActive: !u.isActive } : u));
    
    try {
      await axios.patch(`/system-settings/users/${user.id}/status`, { isActive: !user.isActive });
    } catch (error) {
      console.error('Error updating status:', error);
      // Revert on failure
      setUsers(originalUsers);
      alert('Failed to update status');
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/user-roles')} sx={{ mr: 1 }}>
              Back
            </Button>
            <PersonIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Users
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/system-settings/user-roles/bulk-users')}
            >
              Bulk Upload
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/system-settings/user-roles/users/create')}
            >
              Create User
            </Button>
          </Box>
        </Box>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>

                    <TableCell>{user.email}</TableCell>

                    <TableCell>
                      <Chip label={user.role.name} size="small" variant="outlined" />
                    </TableCell>

                    <TableCell align="center">
  <Switch
    checked={user.isActive}
    color="success"
    onChange={() => handleToggleStatus(user)}
  />
</TableCell>

                    {/* ACTIONS */}
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() =>
                              navigate(`/system-settings/user-roles/users/edit/${user.id}`)
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Reset Password">
                          <IconButton
                            size="small"
                            onClick={() => handleResetPassword(user.id)}
                            color="warning"
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
          <DialogTitle>Password Reset Successful</DialogTitle>
          <DialogContent>
            <DialogContentText>
              The password has been reset. Please share this temporary password with the user:
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                {newPassword}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)} autoFocus>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => !actionLoading && setDeleteDialogOpen(false)}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to <strong>permanently delete</strong> user <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
              <br /><br />
              This action is <strong>PERMANENT</strong> and cannot be undone. The user record will be removed from the database.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={!!actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained" disabled={!!actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default UsersList;
